// Info / Files › Infocenter: the yard's per-vessel folders, as the desktop kept them — a folder per production
// year, in it a folder per vessel ("<SAP> - <NAME>"), and in that the vessel's files, among them its
// serial-numbers "SN - <SAP>.txt". At the top, the season's vessels with their serial numbers (each opens its
// Serial numbers page, pages/SerialNumbers.tsx); below, the folders to browse, upload to and tidy. The path is
// in the URL (?in=infocenter&path=…), so a vessel's Serial numbers page comes back to its folder.

import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { useCreateFolder, useDeleteFile, useFiles, useOverview, useUploadFile } from '../features/hooks.js';
import { infocenterYear, snFileName, useSnVessels, vesselFolderName } from '../features/serialNumbers.js';
import { EmptyState, ErrorState, InlineError, LoadingState, PageHeader, SearchInput, useConfirm } from '../components/ui.js';
import { IconChevronRight, IconFile, IconFolder, IconTrash, IconUpload } from '../components/icons.js';
import { fold, hasText } from '../lib/search.js';
import { useDateFormatter } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';

export const INFOCENTER = 'infocenter/';

/** Info / Files' two sections, at the start of the breadcrumb */
export function RootSwitch({ at, onOrderLists, onInfocenter }: { at: 'orders' | 'infocenter'; onOrderLists: () => void; onInfocenter: () => void }) {
  return (
    <span className="if-roots" role="group" aria-label="Info / Files">
      <button type="button" className={`btn btn-sm ${at === 'orders' ? 'btn-primary' : 'btn-ghost'}`} aria-pressed={at === 'orders'} onClick={onOrderLists}>Order Lists</button>
      <button type="button" className={`btn btn-sm ${at === 'infocenter' ? 'btn-primary' : 'btn-ghost'}`} aria-pressed={at === 'infocenter'} onClick={onInfocenter}>Infocenter</button>
    </span>
  );
}

/** "<SAP> - <NAME>" → its SAP, or '' (a name with no Latin letters or digits leaves "<SAP> -") */
const sapOfFolder = (name: string) => /^(\d{5,})\s+-(?:\s+\S|\s*$)/.exec(name)?.[1] ?? '';

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function Infocenter({ onOrderLists }: { onOrderLists: () => void }) {
  const { can } = useAuth();
  const canWrite = can('production:write');
  const df = useDateFormatter();
  const isPhone = useIsPhone();
  const { activeSeason } = useSeason();
  const [params, setParams] = useSearchParams();
  const raw = params.get('path') ?? '';
  const prefix = raw.startsWith(INFOCENTER) && !raw.includes('..') ? (raw.endsWith('/') ? raw : `${raw}/`) : INFOCENTER;
  const go = (p: string) => setParams(p === INFOCENTER ? { in: 'infocenter' } : { in: 'infocenter', path: p });
  const segs = prefix.slice(INFOCENTER.length).split('/').filter(Boolean);
  const level: 'years' | 'vessels' | 'files' = segs.length === 0 ? 'years' : segs.length === 1 ? 'vessels' : 'files';
  const vesselSap = level === 'files' ? sapOfFolder(segs[1] ?? '') : '';

  const { data, isLoading, error, refetch } = useFiles(prefix);
  const snQ = useSnVessels();
  // the season's vessels come from the Overview, for those who may see it (Info / Files readers may not: then
  // each vessel's Serial numbers is reached from its folder)
  const canOverview = can('overview:read');
  const overview = useOverview(activeSeason || undefined, { enabled: canOverview });
  const vessels = useMemo(() => (overview.data?.vessels ?? []).filter((v) => v.sap && v.name), [overview.data]);
  const upload = useUploadFile();
  const del = useDeleteFile();
  const mkdir = useCreateFolder();
  const fileInput = useRef<HTMLInputElement>(null);
  const [confirmNode, confirm] = useConfirm();
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');

  const yearPrefix = infocenterYear(activeSeason);
  const yearName = yearPrefix.slice(INFOCENTER.length, -1);
  // the season's vessels with no folder anywhere in the Infocenter yet
  const missing = vessels.filter((v) => !snQ.data?.folders[v.sap]);
  const createVesselFolders = async () => {
    if (!missing.length) { setMsg('Every vessel of the season has its folder.'); return; }
    let n = 0;
    for (const v of missing) {
      setMsg(`Creating ${n + 1} of ${missing.length}…`);
      try { await mkdir.mutateAsync({ prefix: yearPrefix, name: vesselFolderName(v.sap, v.name) }); n++; } catch { /* keep going */ }
    }
    void snQ.refetch();
    setMsg(`Created ${n} of ${missing.length} vessel folders in ${yearName}.`);
  };
  const onUpload = async (files: File[]) => {
    let n = 0;
    for (const f of files) {
      const name = f.name.replace(/[^A-Za-z0-9 _.()-]+/g, ' ').replace(/\.{2,}/g, '.').replace(/\s+/g, ' ').trim() || 'file';
      try { await upload.mutateAsync({ prefix, file: name === f.name ? f : new File([f], name, { type: f.type }) }); n++; } catch { /* reported below */ }
    }
    setMsg(`Uploaded ${n} of ${files.length} file${files.length === 1 ? '' : 's'}.`);
    void snQ.refetch();
  };

  const folders = data?.folders ?? [];
  const files = data?.files ?? [];
  const needle = fold(q.trim());
  const listed = vessels.filter((v) => !needle || hasText(needle, v.sap, v.name, v.model));
  const snLink = (sap: string, name?: string, model?: string) =>
    `/serial-numbers/${encodeURIComponent(sap)}?season=${encodeURIComponent(activeSeason)}${name ? `&name=${encodeURIComponent(name)}` : ''}${model ? `&model=${encodeURIComponent(model)}` : ''}`;

  let actions: ReactNode = null;
  if (canWrite && level === 'years') {
    actions = <button className="btn btn-sm" disabled={mkdir.isPending} onClick={() => void mkdir.mutateAsync({ prefix: INFOCENTER, name: yearName }).then(() => setMsg(`The ${yearName} folder is ready.`), () => setMsg('Could not create the year folder.'))}>+ {yearName}</button>;
  } else if (canWrite && canOverview && level === 'vessels') {
    actions = <button className="btn btn-sm btn-primary" disabled={mkdir.isPending || !overview.data || !snQ.data} onClick={() => void createVesselFolders()} title={`One folder per vessel of season ${activeSeason} that has none yet, in ${yearName}`}>{mkdir.isPending ? 'Creating…' : `+ Vessel folders (${missing.length})`}</button>;
  }

  return (
    <>
      <PageHeader title={isPhone ? 'Info / Files' : `Info / Files - ${['Infocenter', ...segs].join(' / ')}`}>
        {actions}
        {vesselSap && <Link className="btn btn-sm btn-primary" to={snLink(vesselSap, segs[1]?.slice(segs[1].indexOf(' - ') + 3))}>Serial numbers</Link>}
        {canWrite && level !== 'years' && (
          <>
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => { const fs = e.target.files; e.target.value = ''; if (fs?.length) void onUpload(Array.from(fs)); }} />
            <button className="btn btn-sm" disabled={upload.isPending} onClick={() => fileInput.current?.click()}>{upload.isPending ? 'Uploading…' : <><IconUpload size={14} /> Upload</>}</button>
          </>
        )}
      </PageHeader>

      <div className="toolbar if-crumbs" style={{ gap: 4 }}>
        <RootSwitch at="infocenter" onOrderLists={onOrderLists} onInfocenter={() => go(INFOCENTER)} />
        {segs.map((s, i) => (
          <span key={s}>
            <span className="muted"> / </span>
            <button className="btn btn-sm btn-ghost" onClick={() => go(`${INFOCENTER}${segs.slice(0, i + 1).join('/')}/`)}>{s}</button>
          </span>
        ))}
      </div>
      <div className="muted" role="status" style={msg ? { marginBottom: 8 } : undefined}>{msg}</div>
      {(del.error ?? mkdir.error ?? upload.error) && <InlineError error={del.error ?? mkdir.error ?? upload.error} onDismiss={() => { del.reset(); mkdir.reset(); upload.reset(); }} />}

      {level === 'years' && (
        <section className="card ic-sn" aria-label="Serial numbers">
          <div className="ic-sn-head">
            <h2>Serial numbers · season {activeSeason}</h2>
            <SearchInput value={q} onChange={setQ} placeholder="SAP, name or model" width={220} />
          </div>
          {!canOverview && <p className="muted">The season’s vessels are listed for those who can see the Overview. Open a vessel’s folder below for its Serial numbers.</p>}
          {((canOverview && overview.isLoading) || snQ.isLoading) && <LoadingState />}
          {snQ.error && <ErrorState error={snQ.error} onRetry={() => void snQ.refetch()} />}
          {overview.error && <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />}
          {overview.data && snQ.data && (
            listed.length ? (
              <div className="tbl-wrap">
                <table className="tbl tbl-cards ic-sn-tbl">
                  <thead><tr><th>Vessel</th><th>Model</th><th>{snFileName('<SAP>')}</th><th /></tr></thead>
                  <tbody>
                    {listed.map((v) => {
                      const f = snQ.data!.index[v.sap];
                      return (
                        <tr key={v.sap}>
                          <td className="tc-title"><span className="mono">{v.sap}</span> · {v.name}</td>
                          <td>{v.model}</td>
                          <td className={f ? '' : 'muted'}>{f ? `Saved ${df.dateTime(f.uploaded)}` : 'None yet'}</td>
                          <td className="tc-actions"><Link className={`btn btn-sm${f ? '' : ' btn-ghost'}`} to={snLink(v.sap, v.name, v.model)}>{f ? 'Open' : canWrite ? 'Create' : 'Open'}</Link></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <p className="muted">{vessels.length ? 'No vessel matches.' : `No vessels on the season ${activeSeason} Overview.`}</p>
          )}
        </section>
      )}

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && (
        <div className="tbl-wrap">
          <table className="tbl tbl-cards if-tbl">
            <thead><tr><th>Name</th><th>Size</th><th>Modified</th><th></th></tr></thead>
            <tbody>
              {folders.map((f) => {
                const name = f.name.replace(/\/$/, '');
                const sap = level === 'vessels' ? sapOfFolder(name) : '';
                const sn = sap ? snQ.data?.index[sap] : undefined;
                return (
                  <tr key={f.prefix} className="if-folder">
                    <td>
                      <button className="btn btn-sm btn-ghost if-folder-btn" onClick={() => go(f.prefix)}>
                        <IconFolder size={15} /> {name}
                        <span className="if-chev" aria-hidden="true"><IconChevronRight size={16} /></span>
                      </button>
                    </td>
                    <td className="muted tc-hide">-</td>
                    <td className="muted tc-hide">{sap ? (sn ? `S/N saved ${df.date(sn.uploaded)}` : 'No S/N file') : '-'}</td>
                    <td className="tc-hide">{sap && <Link className="btn btn-sm btn-ghost" to={snLink(sap, name.slice(name.indexOf(' - ') + 3))}>Serial numbers</Link>}</td>
                  </tr>
                );
              })}
              {files.filter((f) => !f.name.endsWith('.keep')).map((f) => {
                const size = fmtSize(f.size);
                const modified = df.dateTime(f.uploaded);
                const snSap = /^SN - (.+)\.txt$/i.exec(f.name)?.[1]?.trim();
                return (
                  <tr key={f.key}>
                    <td className="tc-title if-name">
                      <IconFile size={15} /> {f.name}
                      <span className="if-file-meta">{size} · {modified}</span>
                    </td>
                    <td className="muted tc-hide">{size}</td>
                    <td className="muted tc-hide">{modified}</td>
                    <td className="tc-actions if-acts" style={{ whiteSpace: 'nowrap' }}>
                      {snSap && <Link className="btn btn-sm" to={snLink(snSap)}>Open</Link>}
                      <a className="btn btn-sm" href={`/api/files/download?key=${encodeURIComponent(f.key)}`}>Download</a>
                      {canWrite && (
                        <button
                          className="btn btn-sm btn-ghost btn-del if-del"
                          aria-label={`Delete ${f.name}`}
                          disabled={del.isPending}
                          onClick={async () => {
                            if (await confirm({ title: 'Delete file', message: `Delete “${f.name}”? This cannot be undone.`, confirmLabel: 'Delete', danger: true })) {
                              await del.mutateAsync(f.key);
                              void snQ.refetch();
                            }
                          }}
                        ><IconTrash size={14} /> Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {folders.length === 0 && files.filter((f) => !f.name.endsWith('.keep')).length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      title={level === 'years' ? 'No year folders yet' : 'This folder is empty'}
                      hint={!canWrite ? undefined
                        : level === 'years' ? `Create the ${yearName} folder to start.`
                        : level === 'vessels' ? 'Use “+ Vessel folders” to make one folder per vessel of the season.'
                        : 'Upload a file, or write the vessel’s serial numbers.'}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {confirmNode}
    </>
  );
}
